import { useSession } from "../auth/SessionProvider";
import { ConsoleKeySection } from "../components/account/ConsoleKeySection";
import { ShellSection } from "../components/account/ShellSection";
import { SshKeySection } from "../components/account/SshKeySection";
import { UsageSection } from "../components/account/UsageSection";
import { PageHeader } from "../components/layout/PageHeader";

/**
 * Your account: the keys that get you into your workspaces.
 *
 * It exists because a key is not a one-off: laptops are replaced, keys are
 * rotated, and a typo in a pasted key produces a workspace that reports "Ready"
 * and answers `Permission denied (publickey)`. Without this screen the only way
 * out of that would be to ask somebody with database access.
 *
 * It holds several keys because a researcher connects from several machines —
 * a laptop, a cluster login node — and each of those has its own key, as it
 * should: the private half is not something to carry around. Each key also
 * says where the console may install it, so the key that sits on a shared
 * login node no longer opens everything the laptop key opens.
 *
 * Two kinds of credential live here, and the difference is worth the two
 * sections: an SSH key opens a machine, a console key drives the console from
 * a terminal. The second one is minted here and nowhere else — the command
 * line can never make its own (`docs/AUTH.md` §7).
 *
 * It also tells you what you held and what it cost (ADR-0025) — information
 * about your own habits, next to the keys that are yours, and never a quota.
 *
 * Returns:
 *     The account screen.
 */
export function AccountPage(): JSX.Element {
  const { me } = useSession();

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Your account"
        subtitle="The SSH keys you connect with and where each one is installed, and the console keys your terminal carries."
        back={{ to: "/", label: "GPUs" }}
      />

      <section className="section" aria-labelledby="account-identity">
        <h2 className="section__title" id="account-identity">
          Who you are
        </h2>
        <p className="aside__meta">
          <span className="mono">{me.username}</span>
          {me.email ? ` · ${me.email}` : ""}
          {me.is_admin ? " · console admin" : ""}
        </p>
        <p className="aside__meta">
          {me.has_home_volume
            ? "Your home folder exists and is kept, whatever happens to the machines."
            : "Your home folder is created the first time you open a workspace."}
        </p>
        {/* What the console can do, not what Slack knows about this person:
            whether an account answers to this address is only learned when a
            message is actually sent, and the workspace's trace is where that
            answer belongs. This line would be lying if it claimed more. */}
        <p className="aside__meta">
          {!me.slack_notifications
            ? "Slack messages: off on this console. Warnings appear on the workspace page."
            : me.email
              ? "Slack messages: on. Warnings about your work are also sent to you there as a direct message, and each workspace's trace says whether it went through."
              : "Slack messages: on for this console, but your account has no e-mail address for Slack to match, so warnings stay on the workspace page."}
        </p>
      </section>

      <section className="section" aria-labelledby="account-usage">
        <h2 className="section__title" id="account-usage">
          Your usage
        </h2>
        <UsageSection />
      </section>

      <section className="section" aria-labelledby="account-key">
        <h2 className="section__title" id="account-key">
          Your SSH keys
        </h2>
        <SshKeySection storedKeys={me.ssh_public_keys} />
      </section>

      <section className="section" aria-labelledby="account-console-keys">
        <h2 className="section__title" id="account-console-keys">
          Your console keys
        </h2>
        <ConsoleKeySection />
      </section>

      <section className="section" aria-labelledby="account-shell">
        <h2 className="section__title" id="account-shell">
          Your shell
        </h2>
        <ShellSection current={me.shell} />
      </section>
    </>
  );
}
